<template>
 <UForm :state="state" :schema="reviewSchema" @submit="onSubmit">
    <div class="space-y-4">
        <UFormField name="rating" :label="$t('Rating')" required>
            <Rating v-model="state.rating" :max="5" />
        </UFormField>

        <UFormField v-if="!review && !customerStore.customer" name="name" :label="$t('Name')" required>
            <UInput class="w-full" v-model="state.name" :placeholder="$t('Your name')" />
        </UFormField>

        <UFormField v-if="!review && !customerStore.customer" name="email" :label="$t('Email')" required>
            <UInput class="w-full" v-model="state.email" :placeholder="$t('Your Email')" />
        </UFormField>

        <UFormField   name="comment" :label="$t('Comment')" required>
            <UTextarea class="w-full" v-model="state.comment" :placeholder="$t('Write your review here...')"  />
        </UFormField>
    </div>

    <div class="mt-6 flex gap-4 justify-end">
        <UButton type="button" :label="$t('Cancel')" variant="soft" color="neutral" @click="$emit('cancel')" />
        <UButton :disabled="pending" type="submit" :label="$t('Submit')" />
    </div>
 </UForm>
</template>
<script setup lang="ts">
const { t: $t } = useI18n()

import Rating from '../molecules/Rating.vue';
import type { FormSubmitEvent } from '@nuxt/ui';
import { z } from 'zod';
import type { ProductReview } from '~/types';

const customerStore = useCustomerStore();

const {
    productId,
    review
} = defineProps<{
    productId: number;
    review?: ProductReview
}>();

const emit =defineEmits<{
    (e: 'review', review: ProductReview): void;
    (e: 'cancel'): void;
}>();

const reviewSchemaBase = z.object({
    rating: z.number().min(1, $t("Rating is required")).max(5, $t("Rating is required")),
    comment: z.string().min(1, $t("Comment is required")).max(2000, $t("Comment must be at most 2000 characters")),
    name: z.string().min(1, $t("Name is required")).max(100, $t("Name must be at most 100 characters")),
    email: z.string().min(1, $t("Email is required")).email($t("Invalid email address")).max(100, $t("Email must be at most 100 characters"))
});

const reviewSchema = review || customerStore.customer
    ? reviewSchemaBase.partial({ name: true, email: true })
    : reviewSchemaBase;

type SchemaOut = z.output<typeof reviewSchema>;

const state = reactive<Partial<SchemaOut>>(review ? {
    rating: review.rating,
    comment: review.comment
} : {
    rating: 0,
    comment: '',
    name: undefined,
    email: undefined
});

const catalogService = useCatalogService();

const pending = ref(false);

const toasts = useToast()

const onSubmit = async (event: FormSubmitEvent<SchemaOut>) => {
    try {
        pending.value = true;

        let newReview : ProductReview;

        if (review) {
           newReview = await catalogService.updateReview(review.id, event.data).then(res => res.data);
        } else {
           newReview = await catalogService.addProductReview({ ...event.data, productId }).then(res => res.data);
        }

        emit('review', newReview);
    } catch (error) {
        console.error('Failed to submit review:', error);

        toasts.add({
            color: 'error',
            title: 'Error',
            description: 'Failed to submit review. Please try again later.'
        });
        return;
    } finally {
        pending.value = false;
    }
};
</script>